import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  signal,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {firstValueFrom, fromEvent, Observable, of} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {ValuationService} from '../../../services/valuation.service';
import {PageInfoService} from '../../../services/page-info.service';
import {DownloadService} from '../../../services/download.service';
import type {CompletedSteps, ValuationSessionResponse} from '../../../models/valuation.models';
import {NgTemplateOutlet} from '@angular/common';
import {ValuationTabComponent} from '../valuation-tab.component';
import {NavigationGuardBypassService} from '../../../routing/navigation-guard-bypass.service';

type TabId = 'property-facts' | 'subject-location' | 'tax-bill' | 'comparables' | 'income-approach' | 'complete';
interface TabDef {
  id: TabId;
  label: string;
  icon: string;
  completedKey?: keyof CompletedSteps;
}

interface WizardProgress extends CompletedSteps {
  completed?: boolean;
}

const DEFAULT_PROGRESS: WizardProgress = {
  property_facts: false,
  subject_location: false,
  tax_bill: false,
  comparables: false,
  income_approach: false,
  completed: false
}

type TabTemplateType = 'actions'
type TabTemplate = [TemplateRef<unknown> | undefined, string, TabTemplateType?];

@Component({
  selector: 'app-valuation-session-page',
  imports: [RouterOutlet, NgTemplateOutlet],
  templateUrl: './valuation-session-page.component.html',
  styleUrl: './valuation-session-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ValuationSessionPageComponent {
  /** Session id from the route, driving the initial (and any subsequent) session load. */
  readonly sessionId = input.required<string>();

  /** Ordered wizard steps -- the single source of truth for tab order, labels/icons,
   * routing, and progress-key lookups. Adding a tab only requires an entry here. */
  protected readonly tabs: TabDef[] = [
    { id: 'property-facts', label: 'Property Facts', icon: 'form-elements', completedKey: "property_facts" },
    { id: 'subject-location', label: 'Subject Location', icon: 'map-pin', completedKey: "subject_location" },
    { id: 'tax-bill', label: 'Tax Bill', icon: 'file-report', completedKey: "tax_bill" },
    { id: 'comparables', label: 'Comparables', icon: 'compare', completedKey: "comparables" },
    { id: 'income-approach', label: 'Income Approach', icon: 'money', completedKey: "income_approach" },
    { id: 'complete', label: 'Complete', icon: 'check' }
  ]

  private readonly valuationService = inject(ValuationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly pageInfo = inject(PageInfoService);
  private readonly downloadService = inject(DownloadService);
  private readonly navigationGuardBypass = inject(NavigationGuardBypassService);

  /** The currently-routed tab's component instance, set by `onActivateTab` via
   * the router outlet's `(activate)` event -- lets this page reach into the
   * active tab for its slot templates below. */
  protected readonly tabComponent = signal<ValuationTabComponent<unknown> | undefined>(undefined)
  /** Slot templates (action bar, header/footer actions) contributed by whichever
   * tab is currently active, projected into this page's shell via `NgTemplateOutlet`. */
  protected readonly tabTemplates = computed<TabTemplate[]>(() => {
    const tc = this.tabComponent()
    if (tc) {
      return [
        [tc.actions(), 'action-bar', 'actions'],
        [tc.headerStart(), 'header-actions-start', 'actions'],
        [tc.headerEnd(), 'header-actions-end', 'actions'],
        [tc.footerStart(), 'footer-start', 'actions'],
        [tc.footerEnd(), 'footer-end', 'actions'],
      ]
    }
    return []
  })

  /** The loaded valuation session, or null before the initial load resolves. */
  readonly session = signal<ValuationSessionResponse | null>(null);
  /** True while a session load (initial or tab-triggered via `load()`) is in flight. */
  readonly loading = signal(true);
  /** User-facing error message from the most recent failed load, if any. */
  readonly loadError = signal<string | null>(null);
  /** Whether the currently-active tab reports itself as valid; set by each tab
   * itself (see `ValuationTabComponent.valid`), read here to gate the "Next" action. */
  readonly tabValid = signal<boolean>(false);
  /** True while the presentation download (`downloadPresentation`) is in flight. */
  protected readonly downloading = signal(false);
  /** Server-owned wizard progress -- seeded from the initial session load,
   * then kept fresh by each tab's own load/save responses via
   * ValuationTabComponent.setData() (every valuation response carries
   * `progress`). This, not `session().progress`, is the live source of truth
   * driving `completedTabs` below. */
  readonly progress = signal<WizardProgress>({ ...DEFAULT_PROGRESS });

  /** Tabs currently unlocked in the sidebar, per server-owned progress.
   * `progress()` is always a real CompletedSteps (defaults to all-false
   * pre-load), so this is never empty -- subject-location (no completedKey)
   * always counts as complete. */
  protected readonly completedTabs: Signal<Set<TabId>> = computed(() => {
    const completed = this.progress();
    return new Set(
      this.tabs
        .filter((tab) => (tab.completedKey ? completed[tab.completedKey] : true))
        .map((tab) => tab.id),
    );
  });

  /** Index of the first not-yet-completed tab (tabs.length if all are complete).
   * Sidebar tabs beyond this index are disabled -- prior steps must be valid
   * before later ones unlock. */
  protected readonly firstIncompleteIndex = computed(() => {
    const completed = this.completedTabs();
    const idx = this.tabs.findIndex((t) => !completed.has(t.id));
    return idx === -1 ? this.tabs.length : idx;
  });

  /** Reflects the currently-active child route (the tab), driven by the URL. */
  protected readonly activeTab = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.currentTabFromRoute()),
    ),
    { initialValue: this.currentTabFromRoute() },
  );
  /** Index of `activeTab` within `tabs` -- drives which tab button/segment is
   * highlighted and is the base for "Next" (`selectTabIndex(activeTabIndex() + 1)`). */
  protected readonly activeTabIndex = computed(() => {
    const id = this.activeTab()
    return this.tabs.findIndex(t => t.id === id)
  });

  /** This page's own `#headerActions` template, published to `PageInfoService`
   * in the constructor so the app shell can render it in the header. */
  private readonly headerActionsTemplate = viewChild.required<TemplateRef<unknown>>('headerActions');

  /** Wires up the three reactive effects this page owns: (1) load the session
   * whenever `sessionId` changes, aborting any in-flight load; (2) recompute
   * `progress` whenever `session` changes; (3) publish this page's header
   * actions template to `PageInfoService` for the duration this page is alive. */
  constructor() {
    effect((onCleanup) => {
      const sessionId = this.sessionId();
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      void this.loadSession(sessionId, controller.signal);
    });

    effect(() => {
      this.setProgress(this.session()?.progress)
    })

    effect((onCleanup) => {
      this.pageInfo.headerActions.set(this.headerActionsTemplate());
      onCleanup(() => this.pageInfo.headerActions.set(null));
    });
  }

  /** Router outlet `(activate)` handler -- captures the freshly-routed tab
   * component instance so `tabTemplates` can pull its slot templates. */
  onActivateTab(tab: unknown) {
    this.tabComponent.set(tab as ValuationTabComponent<unknown>);
  }

  /** Runs `fn`, driving the shared `loading`/`loadError` state and aborting
   * via `abortSignal` if given. Used by both the initial session load and
   * ad-hoc loads triggered by individual tabs. Resolves to null on abort. */
  async load<T>(fn: Observable<T>, abortSignal?: AbortSignal): Promise<T | null> {
    this.loading.set(true);
    this.loadError.set(null);

    const abort$ = abortSignal ? fromEvent(abortSignal, 'abort') : undefined;
    if (abort$) {
      fn = fn.pipe(takeUntil(abort$));
    }

    try {
      const result = await firstValueFrom(fn, {defaultValue: null});
      if (abortSignal?.aborted) return null;
      return result;
    } catch (e) {
      if (abortSignal?.aborted) return null;
      throw e
    } finally {
      this.loading.set(false);
    }
  }

  /** Normalizes a `CompletedSteps` payload (or none, pre-load) into `WizardProgress`
   * and derives `completed` from the last real step before the "Complete" tab. */
  protected setProgress(completed?: CompletedSteps): void {
      const p: WizardProgress = completed ?? { ...DEFAULT_PROGRESS }
      // Completed if last "real" step is completed
      p.completed = p[this.tabs[this.tabs.length - 2].completedKey!]
      this.progress.set(p)
  }

  /** Refreshes `progress` from a cheap GET /{session_id} call, without
   * touching `loading`/`loadError` (this is a lightweight background sync,
   * not a primary page load). Needed for subject-location specifically --
   * its only endpoint (GET .../map) returns a raw image Blob, so it can't
   * carry `progress` inline the way every other tab response does. */
  async refreshProgress(): Promise<void> {
    try {
      const session = await firstValueFrom(this.valuationService.getSession(this.sessionId()));
      this.setProgress(session.progress);
    } catch (e) {
      console.error('Failed to refresh progress.', e);
    }
  }

  /** Primary session load for a given `sessionId`: fetches the session via
   * `load()`, stores it, and attempts the furthest-incomplete-tab redirect. */
  private async loadSession(sessionId: string, signal: AbortSignal): Promise<void> {
    let session: ValuationSessionResponse | null;
    try {
      session = await this.load(this.valuationService.getSession(sessionId), signal);
    } catch (e) {
      console.error('Failed to load session.', e);
      this.loadError.set('Failed to load session.');
      return;
    }
    if (!session) return;

    this.session.set(session);
    try {
      this.redirectToFurthestIncompleteTab(session.progress);
    } catch (e) {
      // Non-critical -- session already loaded and displayed successfully.
      console.error('Failed to redirect to furthest incomplete tab.', e);
    }
  }

  /** If the route landed on the default property-facts tab, jump to the
   * furthest-valid tab instead (docs/valuation-feedback-2026-07-09.md task 5)
   * -- doesn't override an explicit deep-link to a different tab. */
  private redirectToFurthestIncompleteTab(completedSteps: CompletedSteps): void {
    const validByTab: Record<TabId, boolean> = {
      'property-facts': completedSteps.property_facts,
      'subject-location': true,
      'tax-bill': completedSteps.tax_bill,
      comparables: completedSteps.comparables,
      'income-approach': completedSteps.income_approach,
      complete: true
    };

    const firstInvalidIndex = this.tabs.findIndex((t) => !validByTab[t.id]);

    if (this.currentTabFromRoute() !== 'property-facts') return;
    const targetIndex = firstInvalidIndex === -1 ? this.tabs.length - 1 : firstInvalidIndex;
    const targetTab = this.tabs[targetIndex].id;
    if (targetTab !== 'property-facts') {
      void this.router.navigate([targetTab], { relativeTo: this.route });
    }
  }

  /** Navigates to the given tab's child route, relative to this page. */
  selectTab(tab: TabId): void {
    void this.router.navigate([tab], { relativeTo: this.route });
  }

  /** Called reactively by each tab (inside its own `effect()`) to set the page
   *  heading as "<address>, <city> - <tabName>" once property facts are loaded. */
  setTabTitle(tabName: string): void {
    const loc = this.session()?.location;
    const address = loc ? loc.address + (loc.city ? `, ${loc.city}` : '') : null;
    this.pageInfo.heading.set(address ? `${address} - ${tabName}` : tabName);
  }

  /** Resolves the currently-routed child path to a known `TabId`, falling back
   * to `'property-facts'` if there's no matching entry in `tabs` (e.g. no child
   * route yet resolved). */
  private currentTabFromRoute(): TabId {
    const path = this.route.snapshot.firstChild?.routeConfig?.path;
    return this.tabs.find((t) => t.id === path)?.id ?? 'property-facts';
  }

  /** Confirms with the user, then permanently deletes this session and
   * navigates back to the valuations list, bypassing the unsaved-changes guard. */
  protected async confirmDiscard(): Promise<void> {
    if (!window.confirm('Permanently delete this valuation session? This cannot be undone.')) return;
    const deleted = await this.load(
      this.valuationService.deleteSession(this.sessionId()).pipe(
        map(() => true),
        catchError((e) => {
          console.error('Failed to discard session.', e);
          this.loadError.set('Failed to discard session.');
          return of(false);
        }),
      ),
    );
    if (deleted) {
      this.navigationGuardBypass.requestBypass();
      await this.router.navigate(['/valuations']);
    }
  }

  /** Downloads the generated presentation for this session as `presentation.pptx`. */
  async downloadPresentation(): Promise<void> {
    if (this.downloading()) return;
    this.downloading.set(true);
    this.loading.set(true);
    try {
      await this.downloadService.download(
        this.valuationService.downloadValuation(this.sessionId()),
        'presentation.pptx',
      );
    } catch (e) {
      console.error('Failed to download presentation.', e);
    } finally {
      this.downloading.set(false);
      this.loading.set(false);
    }
  }

  /** Navigates to the tab at `newIndex` in `tabs` -- the shared handler behind
   * both sidebar tab clicks and the "Next"/"Back" actions. */
  protected selectTabIndex(newIndex: number): void {
    const newTab = this.tabs[newIndex];
    this.selectTab(newTab.id);
  }
}
